import {
  useMutation,
  useQueryClient,
  UseMutationOptions,
  MutationFunction,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAlert } from 'routes/AppContainer/actions';
import { selectCurrentIdentityLanguage } from 'routes/AppContainer/selectors';
import Helium from 'utils/helium';
import { queryRequest } from 'utils/queryRequest';
import * as Sentry from '@sentry/react';

export type MutationArgs<
  T extends keyof Api.ClientSpec = keyof Api.ClientSpec,
> = Api.ClientSpec[T]['pathParams'] extends []
  ? {
      apiEndpointRequirements?: never;
      payload?: ApiClientPayload<T>;
      query?: ApiClientQuery<T>;
      [key: string]: any;
    }
  : {
      apiEndpointRequirements: Api.ClientSpec[T]['pathParams'];
      payload?: ApiClientPayload<T>;
      query?: ApiClientQuery<T>;
      [key: string]: any;
    };

export type ApiClientResponse<T extends keyof Api.ClientSpec> = Awaited<
  ReturnType<Api.Client[T]>
>['json']['data'];

type ApiClientPayload<T extends keyof Api.ClientSpec = keyof Api.ClientSpec> =
  Api.ClientSpec[T]['payloadOffset'] extends -1
    ? never
    : Parameters<Api.Client[T]>[Api.ClientSpec[T]['payloadOffset']];

type ApiClientQuery<T extends keyof Api.ClientSpec = keyof Api.ClientSpec> =
  Api.ClientSpec[T]['queryOffset'] extends -1
    ? never
    : Parameters<Api.Client[T]>[Api.ClientSpec[T]['queryOffset']];

interface BaseOpMutationOptions<TData, TVariables> {
  onSuccessMessage?:
    | string
    | ((mutationResponse: TData, mutationArgs: TVariables) => string)
    | null;
  onSuccessCallback?: (
    mutationResponse: TData,
    mutationArgs: TVariables,
  ) => void | Promise<void>;
  onErrorMessage?: string | ((error: OP.ApiError) => string) | null;
  shouldSuppressErrorMessage?:
    | boolean
    | ((error: OP.ApiError) => boolean | void);
  onErrorCallback?: (
    error: OP.ApiError,
    variables?: TVariables,
  ) => void | Promise<void>;
  queryKeysToInvalidate?: (string | number)[][];
  headers?: Record<string, string>;
  overrideApiClient?: typeof Helium.client;
  catchMutateAsyncError?: boolean;
  bypassTriggers?: boolean;
}
interface UseOpMutationOptionsWithoutApiEndpointName<
  TData extends any = any,
  TVariables extends any = any,
> extends UseMutationOptions<TData, OP.ApiError, TVariables>,
    BaseOpMutationOptions<TData, TVariables> {
  apiEndpointName?: undefined;
}

interface UseOpMutationOptionsWithApiEndpointName<
  T extends keyof Api.ClientSpec,
  TData extends any = any,
  TVariables extends any = any,
> extends UseMutationOptions<TData, OP.ApiError, TVariables>,
    BaseOpMutationOptions<TData, TVariables> {
  apiEndpointName: T;
}

type UseOpMutationOptions<
  T extends keyof Api.ClientSpec,
  TData extends unknown = ApiClientResponse<T>,
  TVariables extends unknown = MutationArgs<T>,
> =
  | UseOpMutationOptionsWithApiEndpointName<T, TData, TVariables>
  | UseOpMutationOptionsWithoutApiEndpointName<TData, TVariables>;

export const useOpMutation = <
  T extends keyof Api.ClientSpec,
  TData extends unknown = ApiClientResponse<T>,
  TVariables extends unknown = MutationArgs<T>,
>({
  apiEndpointName,
  onSuccessMessage,
  onErrorMessage,
  onSuccessCallback,
  onErrorCallback, // Will only affect mutateAsync (need to prevent mutate using types in separate PR)
  queryKeysToInvalidate,
  shouldSuppressErrorMessage = false,
  mutationKey,
  mutationFn,
  onError: _onError,
  onSuccess: _onSuccess,
  // TODO fix so we can add these back
  onMutate,
  onSettled,
  headers,
  overrideApiClient,
  catchMutateAsyncError = true,
  bypassTriggers,
  ...useOpMutationOptions
}: UseOpMutationOptions<T, TData, TVariables>) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // TODO - get the language based on authed page logic if not in identity
  /** we first look at the identity language and set the language to that.
   * If that is not set, we then fallback to the local storage setting.
   * If that is not set, then we fallback to the browser locale.
   * And if that is not supported then we use English (en). */
  const currentIdentityLanguage = useSelector(selectCurrentIdentityLanguage());

  const finalMutationFn =
    mutationFn ??
    ((async ({
      apiEndpointRequirements = [],
      payload: rawPayload,
      query: rawQuery,
    }: MutationArgs<T>): Promise<ApiClientResponse<T>> => {
      const parametersArray: Parameters<Api.Client[T]>[number][] = [
        ...apiEndpointRequirements,
      ];

      if (rawPayload) {
        parametersArray.push(rawPayload);
      }
      if (rawQuery) {
        parametersArray.push(rawQuery);
      }

      const result = await queryRequest({
        apiEndpointName: apiEndpointName!,
        parameters: parametersArray as Parameters<Api.Client[T]>,
        currentIdentityLanguage,
        headers,
        overrideApiClient,
        bypassTriggers,
      });

      if (result.statusCode >= 400) {
        throw result.json;
      }

      return result.json?.data;

      // TODO: Figure out how to do this method without needing the typecast
    }) as MutationFunction<TData, TVariables>);

  const customOnError = (error: OP.ApiError) => {
    const errorMessage =
      typeof onErrorMessage === 'string'
        ? onErrorMessage
        : onErrorMessage
          ? onErrorMessage(error)
          : undefined;

    if (
      (typeof shouldSuppressErrorMessage === 'boolean' &&
        !shouldSuppressErrorMessage) ||
      (typeof shouldSuppressErrorMessage === 'function' &&
        !shouldSuppressErrorMessage(error))
    ) {
      dispatch(setAlert('error', errorMessage || error.message));
    }
  };

  const customOnSuccess = async (
    mutationResponse: TData,
    mutationArgs: TVariables,
  ) => {
    const successMessage =
      typeof onSuccessMessage === 'string'
        ? onSuccessMessage
        : onSuccessMessage
          ? onSuccessMessage(mutationResponse, mutationArgs)
          : undefined;

    if (successMessage) {
      dispatch(setAlert('success', successMessage));
    }

    if (onSuccessCallback) {
      await onSuccessCallback(mutationResponse, mutationArgs);
    }

    if (queryKeysToInvalidate) {
      queryKeysToInvalidate.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    }
  };

  const {
    mutateAsync: rawMutateAsync,
    // mutate: _mutate, // TODO - uncomment in PR to prevent mutate from being used
    ...rest
  } = useMutation<TData, OP.ApiError, TVariables>({
    mutationKey: mutationKey || apiEndpointName ? [apiEndpointName] : undefined,
    mutationFn: finalMutationFn,
    onError: (_onError || customOnError) as (error: OP.ApiError) => void,
    onSuccess: (_onSuccess || customOnSuccess) as (
      data: TData,
      variables: TVariables,
      context: unknown,
    ) => unknown,
    onMutate,
    onSettled: onSettled as (
      data: TData | undefined,
      error: OP.ApiError | null,
      variables: TVariables,
      context: unknown,
    ) => unknown,
    ...useOpMutationOptions,
  });

  const mutateAsync = useCallback<typeof rawMutateAsync>(
    async (variables, options) => {
      try {
        return await rawMutateAsync(variables, options);
      } catch (error) {
        const OpError = error as OP.ApiError;

        if (OpError.statusCode >= 500) {
          Sentry.captureException(OpError);
        }

        if (onErrorCallback) {
          await onErrorCallback(OpError, variables);
        }

        return undefined as TData;
      }
    },
    [onErrorCallback, rawMutateAsync],
  );

  return {
    /**
     * When using useOpMutation directly, we want to catch the error in the custom
     * mutateAsync function so that we don't need to wrap each call to mutateAsync
     * in a try/catch block.
     *
     * However, when using useOpMutation as a hook in another useOpMutation hook
     * within the mutationFn, we want to allow the error to bubble up so that the
     * parent hook can handle the error and block in subsequent code from running.
     */
    mutateAsync: catchMutateAsyncError ? mutateAsync : rawMutateAsync,
    ...rest,
  };
};
